import { useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { BookmarkSimple as BookmarkIcon } from '@phosphor-icons/react/dist/ssr/BookmarkSimple'
import { DownloadSimple as DownloadIcon } from '@phosphor-icons/react/dist/ssr/DownloadSimple'
import { Image as ImageIcon } from '@phosphor-icons/react/dist/ssr/Image'
import { Link as RouterLink } from 'react-router-dom'

import {
	countDigestTasksDone,
	digestTaskKey,
	digestTaskKeysFromItems,
	isDigestTaskDone,
	setDigestTaskDone,
} from '@/lib/assistant-digest-tasks'
import { captureElementAsPng, downloadCsv } from '@/lib/assistant-export'
import { normalizeAssistantNavigatePath } from '@/lib/assistant-navigate'
import type { OpsDigestData } from '@/lib/assistant-ops-digest'
import { isPromptPinned, pinPrompt } from '@/lib/assistant-pinned-prompts'

interface ChatOpsDigestProps {
	data: OpsDigestData
}

const BRIEFING_PROMPT = 'What needs my attention today?'

const CATEGORY_LABEL: Record<string, string> = {
	stockout: 'Stockout',
	restock_overdue: 'Restock',
	failed_payment: 'Payments',
	revenue_drop: 'Revenue',
	underperforming_cabinet: 'Cabinet',
}

function categoryColor(category: string): 'error' | 'warning' | 'info' | 'default' {
	if (category === 'stockout' || category === 'failed_payment') return 'error'
	if (category === 'restock_overdue' || category === 'revenue_drop') return 'warning'
	return 'info'
}

export function ChatOpsDigest({ data }: ChatOpsDigestProps) {
	const exportRef = useRef<HTMLDivElement>(null)
	const [pinned, setPinned] = useState(() => isPromptPinned(BRIEFING_PROMPT))
	const [, setTaskTick] = useState(0)
	const taskKeys = digestTaskKeysFromItems(data.items)
	const taskProgress = countDigestTasksDone(taskKeys)

	const handlePinBriefing = () => {
		pinPrompt(BRIEFING_PROMPT)
		setPinned(true)
	}

	const handleExportCsv = () => {
		const rows = [
			['Category', 'Title', 'Detail'],
			...data.items.map((item) => [item.category, item.title, item.detail]),
		]
		downloadCsv('ops-digest.csv', rows)
	}

	const handleExportPng = async () => {
		if (!exportRef.current) return
		await captureElementAsPng(exportRef.current, 'ops-digest.png')
	}

	return (
		<Box
			ref={exportRef}
			sx={{
				mt: 1.5,
				p: 1.25,
				borderRadius: 1.5,
				border: '1px solid',
				borderColor: 'divider',
				bgcolor: 'background.default',
			}}
		>
			<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
				<Box>
					<Typography sx={{ fontWeight: 600, fontSize: '0.8125rem' }} variant="body2">
						What needs attention
					</Typography>
					{taskProgress.total > 0 ? (
						<Typography color="text.secondary" sx={{ fontSize: '0.6875rem' }} variant="caption">
							{taskProgress.done}/{taskProgress.total} done today
						</Typography>
					) : null}
				</Box>
				<Stack direction="row" spacing={0.25}>
					{!pinned ? (
						<Tooltip title="Pin daily briefing">
							<IconButton aria-label="Pin daily briefing" onClick={handlePinBriefing} size="small">
								<BookmarkIcon size={16} />
							</IconButton>
						</Tooltip>
					) : null}
					<Tooltip title="Export CSV">
						<IconButton aria-label="Export digest CSV" onClick={handleExportCsv} size="small">
							<DownloadIcon size={16} />
						</IconButton>
					</Tooltip>
					<Tooltip title="Export image">
						<IconButton aria-label="Export digest image" onClick={() => void handleExportPng()} size="small">
							<ImageIcon size={16} />
						</IconButton>
					</Tooltip>
				</Stack>
			</Stack>
			<Typography color="text.secondary" sx={{ fontSize: '0.75rem', mb: 1 }} variant="caption">
				{data.summary}
			</Typography>

			{data.items.length ? (
				<Stack spacing={0.75} sx={{ mb: 1 }}>
					{data.items.map((item, index) => {
						const taskKey = digestTaskKey(item.category, item.title, item.site_id)
						const done = isDigestTaskDone(taskKey)
						return (
							<Stack key={`${item.category}-${index}`} direction="row" spacing={1} alignItems="flex-start">
								<Checkbox
									checked={done}
									inputProps={{ 'aria-label': `Mark ${item.title} done` }}
									onChange={(_, checked) => {
										setDigestTaskDone(taskKey, checked)
										setTaskTick((n) => n + 1)
									}}
									size="small"
									sx={{ p: 0.25, mt: -0.25, ml: -0.25 }}
								/>
								<Chip
									color={categoryColor(item.category)}
									label={CATEGORY_LABEL[item.category] ?? item.category}
									size="small"
									sx={{ height: 20, fontSize: '0.625rem', mt: 0.1, flexShrink: 0, opacity: done ? 0.5 : 1 }}
								/>
								<Box sx={{ flex: 1, minWidth: 0, opacity: done ? 0.55 : 1 }}>
									<Typography
										sx={{
											fontSize: '0.75rem',
											fontWeight: 600,
											lineHeight: 1.35,
											textDecoration: done ? 'line-through' : 'none',
										}}
										variant="body2"
									>
										{item.title}
									</Typography>
									<Typography color="text.secondary" sx={{ fontSize: '0.6875rem', lineHeight: 1.35 }} variant="caption">
										{item.detail}
									</Typography>
								</Box>
								{item.navigate ? (
									<Chip
										clickable
										color="primary"
										component={RouterLink}
										label="Open"
										size="small"
										sx={{ flexShrink: 0, height: 22, fontSize: '0.625rem', opacity: done ? 0.5 : 1 }}
										to={normalizeAssistantNavigatePath(item.navigate.path)}
										variant="outlined"
									/>
								) : null}
							</Stack>
						)
					})}
				</Stack>
			) : (
				<Typography color="text.secondary" sx={{ fontSize: '0.75rem', mb: 1 }} variant="body2">
					No urgent issues in scope for the last 7 days.
				</Typography>
			)}

			{data.actions.length ? (
				<Stack direction="row" flexWrap="wrap" spacing={0.5} useFlexGap>
					{data.actions.map((action) => (
						<Chip
							key={action.path}
							clickable
							color="primary"
							component={RouterLink}
							label={action.label}
							size="small"
							to={normalizeAssistantNavigatePath(action.path)}
							variant="outlined"
						/>
					))}
				</Stack>
			) : null}
		</Box>
	)
}
